import {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {WORKFORCE_DASHBOARD_SCROLLABLE_SECTION_MAX_HEIGHT} from './workforceDashboardLayoutConstants';
import {WorkforceDashboardRoleCoverageHeatmapRow} from './WorkforceDashboardRoleCoverageHeatmapRow';
import type {WorkforceDashboardRoleCoverageHeatmapTableRow} from './WorkforceDashboardRoleCoverageHeatmapRow';
import {WorkforceDashboardSectionCard} from './WorkforceDashboardSectionCard';
import {BasicTable} from '../../../../../components/BasicTable/BasicTable';
import {createBasicTableColumn} from '../../../../../components/BasicTable/createBasicTableColumn';
import {EmptyStateCard} from '../../../../../components/EmptyStateCard/EmptyStateCard';
import type {WorkforceDashboardRoleCoverageRow} from '../../types/workforceDashboardTypes';

export interface Props {
  data: WorkforceDashboardRoleCoverageRow[] | null;
}

export const WorkforceDashboardRoleCoverageHeatmap = ({data}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDashboardPage.sections.roleCoverageHeatmap'});

  const columns = useMemo(
    () => [
      createBasicTableColumn<WorkforceDashboardRoleCoverageHeatmapTableRow>(
        'targetRole',
        t('columns.targetRole'),
        {
          sortable: true,
          getSortValue: (row) => row.targetProfileTitle,
        }
      ),
      createBasicTableColumn<WorkforceDashboardRoleCoverageHeatmapTableRow>('profiles', t('columns.profiles'), {
        align: 'center',
        sortable: true,
        getSortValue: (row) => row.profileCount,
      }),
      createBasicTableColumn<WorkforceDashboardRoleCoverageHeatmapTableRow>(
        'coreCompetencies',
        t('columns.coreCompetencies'),
        {
          align: 'center',
          sortable: true,
          getSortValue: (row) => row.averageScores.coreCompetencies,
        }
      ),
      createBasicTableColumn<WorkforceDashboardRoleCoverageHeatmapTableRow>('hardSkills', t('columns.hardSkills'), {
        align: 'center',
        sortable: true,
        getSortValue: (row) => row.averageScores.hardSkills,
      }),
      createBasicTableColumn<WorkforceDashboardRoleCoverageHeatmapTableRow>('softSkills', t('columns.softSkills'), {
        align: 'center',
        sortable: true,
        getSortValue: (row) => row.averageScores.softSkills,
      }),
      createBasicTableColumn<WorkforceDashboardRoleCoverageHeatmapTableRow>(
        'responsibilities',
        t('columns.responsibilities'),
        {
          align: 'center',
          sortable: true,
          getSortValue: (row) => row.averageScores.responsibilities,
        }
      ),
      createBasicTableColumn<WorkforceDashboardRoleCoverageHeatmapTableRow>('overall', t('columns.overall'), {
        align: 'center',
        sortable: true,
        getSortValue: (row) => row.averageScores.overall,
      }),
    ],
    [t]
  );

  const rowData = useMemo<WorkforceDashboardRoleCoverageHeatmapTableRow[]>(
    () =>
      (data ?? []).map((item) => ({
        ...item,
        id: item.targetProfileTitle,
      })),
    [data]
  );

  if (!data || data.length === 0) {
    return (
      <WorkforceDashboardSectionCard title={t('title')}>
        <EmptyStateCard />
      </WorkforceDashboardSectionCard>
    );
  }

  return (
    <WorkforceDashboardSectionCard title={t('title')} description={t('description') as string}>
      <BasicTable
        columns={columns}
        rowData={rowData}
        Row={WorkforceDashboardRoleCoverageHeatmapRow}
        hideLoadMoreFooter
        tableContainerSx={{
          overflowX: 'auto',
          maxHeight: WORKFORCE_DASHBOARD_SCROLLABLE_SECTION_MAX_HEIGHT,
          overflowY: 'auto',
        }}
        tableSx={{minWidth: 560}}
      />
    </WorkforceDashboardSectionCard>
  );
};
